import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    TextField,
    Button,
    Stack,
    Accordion,
    AccordionSummary,
    Typography,
    Breadcrumbs,
    Link,
    FormControlLabel,
    Checkbox,
    IconButton,
    CircularProgress,
    Box
} from '@mui/material';
import { ArrowDropDown, DragIndicator, ArrowBack } from '@mui/icons-material';
import { fetchAuthSession } from 'aws-amplify/auth';
import { v4 as uuidv4 } from 'uuid';

type Question = {
    id: number;
    question: string;
    answer: string;
}

type Trivia = {
    id: number;
    trivia: string;
}

const EditGame: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNewGame = id === 'new';

    const [loading, setLoading] = useState(!isNewGame);
    const [saving, setSaving] = useState(false);
    const [gameId, setGameId] = useState(isNewGame ? uuidv4() : id || '');
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [questions, setQuestions] = useState<Question[]>(generateDefaultQuestions());
    const [trivia, setTrivia] = useState<Trivia[]>([]);
    const [ready, setReady] = useState(false);
    const [draggedTriviaIndex, setDraggedTriviaIndex] = useState<number | null>(null);

    useEffect(() => {
        if (!isNewGame && id) {
            loadGame(id);
        }
    }, [id, isNewGame]);

    const loadGame = async (gameId: string) => {
        try {
            setLoading(true);
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            if (!token) {
                console.error('No authentication token available');
                return;
            }

            const response = await fetch(`https://api.admin.famousinitials.com/games/${gameId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to load game');
            }

            // Populate form with game data
            setGameId(data.gameid);
            setFirstName(data.firstname || '');
            setMiddleName(data.middlename || '');
            setLastName(data.lastname || '');
            setReady(data.ready || false);

            // Handle questions
            if (data.questions?.standard && Array.isArray(data.questions.standard)) {
                setQuestions(data.questions.standard.map((q: any) => ({
                    id: q.id || 0,
                    question: q.question || '',
                    answer: q.answer || ''
                })));
            }

            // Handle trivia
            if (Array.isArray(data.trivia)) {
                setTrivia(data.trivia.map((t: any) => ({
                    id: t.id || 0,
                    trivia: t.trivia || ''
                })));
            }
        } catch (error) {
            console.error('Error loading game:', error);
            alert('Failed to load game. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const saveGame = async () => {
        if (questions.filter(q => q.question === '').length > 0 || questions.filter(q => q.answer === '').length > 0) {
            alert('All standard questions must have a question and answer');
            return;
        }

        setSaving(true);

        const gameQuestions = { standard: questions, unique: [] };
        const game = {
            id: gameId,
            firstName,
            middleName,
            lastName,
            ready,
            questions: gameQuestions,
            trivia
        };

        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            if (!token) {
                console.error('No authentication token available');
                alert('Authentication error. Please refresh and try again.');
                return;
            }

            const method = isNewGame ? 'POST' : 'PUT';
            const response = await fetch('https://api.admin.famousinitials.com/games', {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                },
                body: JSON.stringify(game)
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to save game');
            }

            alert('Game saved successfully!');
            navigate('/manage-games');
        } catch (error) {
            console.error('Error saving game:', error);
            alert('Failed to save game. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const deleteGame = async () => {
        if (!confirm(`Are you sure you want to delete ${firstName} ${lastName}?`)) {
            return;
        }

        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            if (!token) {
                console.error('No authentication token available');
                return;
            }

            const response = await fetch(`https://api.admin.famousinitials.com/games/${gameId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to delete game');
            }

            alert('Game deleted successfully');
            navigate('/manage-games');
        } catch (error) {
            console.error('Error deleting game:', error);
            alert('Failed to delete game. Please try again.');
        }
    };

    const setAnswer = (value: string, index: number) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index] = { ...updatedQuestions[index], answer: value };
        setQuestions(updatedQuestions);
    };

    const setTriviaValue = (value: string, index: number) => {
        const updatedTrivia = [...trivia];
        updatedTrivia[index] = { ...updatedTrivia[index], trivia: value };
        setTrivia(updatedTrivia);
    };

    const deleteTrivia = (index: number) => {
        setTrivia(trivia.filter((_, i) => i !== index));
    };

    const handleTriviaDragStart = (index: number) => {
        setDraggedTriviaIndex(index);
    };

    const handleTriviaDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
    };

    const handleTriviaDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();
        if (draggedTriviaIndex === null || draggedTriviaIndex === dropIndex) {
            return;
        }

        const newTrivia = [...trivia];
        const [draggedItem] = newTrivia.splice(draggedTriviaIndex, 1);
        newTrivia.splice(dropIndex, 0, draggedItem);

        setTrivia(newTrivia);
        setDraggedTriviaIndex(null);
    };

    const handleTriviaDragEnd = () => {
        setDraggedTriviaIndex(null);
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs>
                    <Link
                        component="button"
                        variant="body1"
                        onClick={() => navigate('/manage-games')}
                        sx={{ cursor: 'pointer', textDecoration: 'none' }}
                    >
                        Manage Games
                    </Link>
                    <Typography color="text.primary">
                        {isNewGame ? 'New Game' : `Edit: ${firstName} ${lastName}`}
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Paper sx={{ p: 3 }}>
                <Stack spacing={3}>
                    <Typography variant="h5">
                        {isNewGame ? 'Create New Game' : 'Edit Game'}
                    </Typography>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="First Name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Middle Name"
                            value={middleName}
                            onChange={(e) => setMiddleName(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Last Name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            fullWidth
                        />
                    </Stack>

                    <FormControlLabel
                        label="Ready to Use"
                        control={<Checkbox checked={ready} onChange={() => setReady(!ready)} />}
                    />

                    <Accordion>
                        <AccordionSummary expandIcon={<ArrowDropDown />}>
                            <Typography>Standard Questions</Typography>
                        </AccordionSummary>
                        <Stack spacing={2} sx={{ p: 2 }}>
                            {questions.map((question, index) => (
                                <Stack key={question.id} spacing={2} direction="row" width="100%">
                                    <TextField
                                        label="Question"
                                        variant="standard"
                                        value={question.question}
                                        disabled
                                        fullWidth
                                    />
                                    <TextField
                                        label="Answer"
                                        variant="standard"
                                        value={question.answer}
                                        onChange={(e) => setAnswer(e.target.value, index)}
                                        fullWidth
                                    />
                                </Stack>
                            ))}
                        </Stack>
                    </Accordion>

                    <Accordion>
                        <AccordionSummary expandIcon={<ArrowDropDown />}>
                            <Typography>Trivia</Typography>
                        </AccordionSummary>
                        <Stack spacing={2} sx={{ p: 2 }}>
                            {trivia.map((item, index) => (
                                <Stack
                                    key={item.id}
                                    spacing={2}
                                    direction="row"
                                    width="100%"
                                    draggable
                                    onDragStart={() => handleTriviaDragStart(index)}
                                    onDragOver={(e) => handleTriviaDragOver(e, index)}
                                    onDrop={(e) => handleTriviaDrop(e, index)}
                                    onDragEnd={handleTriviaDragEnd}
                                    sx={{
                                        cursor: 'grab',
                                        opacity: draggedTriviaIndex === index ? 0.5 : 1,
                                        '&:active': { cursor: 'grabbing' }
                                    }}
                                >
                                    <IconButton size="small" sx={{ cursor: 'grab' }}>
                                        <DragIndicator />
                                    </IconButton>
                                    <TextField
                                        label="Trivia"
                                        variant="standard"
                                        value={item.trivia}
                                        onChange={(e) => setTriviaValue(e.target.value, index)}
                                        fullWidth
                                    />
                                    <Button variant="outlined" onClick={() => deleteTrivia(index)}>
                                        Delete
                                    </Button>
                                </Stack>
                            ))}
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    setTrivia([
                                        ...trivia,
                                        {
                                            id: Math.max(...questions.map(o => o.id).concat(trivia.map(o => o.id)), 0) + 1,
                                            trivia: ''
                                        }
                                    ])
                                }
                            >
                                Add Trivia
                            </Button>
                        </Stack>
                    </Accordion>

                    <Stack direction="row" spacing={2} justifyContent="space-between">
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBack />}
                            onClick={() => navigate('/manage-games')}
                        >
                            Back to List
                        </Button>
                        <Stack direction="row" spacing={2}>
                            {!isNewGame && (
                                <Button variant="outlined" color="error" onClick={deleteGame}>
                                    Delete
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={saveGame}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Game'}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Paper>
        </Container>
    );
};

function generateDefaultQuestions(): Question[] {
    const questions = [] as Question[];
    questions.push({ id: 0, question: 'What are they famous for?', answer: '' });
    questions.push({ id: 1, question: 'Are they a child or adult?', answer: '' });
    questions.push({ id: 2, question: 'What is their gender?', answer: '' });
    questions.push({ id: 3, question: 'What is their nationality?', answer: '' });
    questions.push({ id: 4, question: 'Are they married?', answer: '' });
    questions.push({ id: 5, question: 'Are they real or fictional?', answer: '' });
    return questions;
}

export default EditGame;
